import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import {useMediaQuery} from "@mantine/hooks";
import {Fragment, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {twJoin} from "tailwind-merge";
import {KanbanCardSkeleton} from "~/components/ottopilotDataViewer/kanbanCardSkeleton";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {ListViewInfiniteRows} from "~/components/ottopilotDataViewer/listViewRows";
import {TableNoDataFoundComponent} from "~/components/ottopilotDataViewer/tableNoDataFoundComponent";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import type {ViewType} from "~/components/ottopilotDataViewer/view";
import {
    LIST_CARD_NARROW_CONTAINER_MAX_WIDTH_PX,
    resolveOttopilotListCardLayout,
    type OttopilotView,
} from "~/components/ottopilotDataViewer/view";
import {useAsidePanelContextSafe} from "~/contexts/asidePanelContext";
import type {useListViewRows} from "~/components/ottopilotDataViewer/viewDataHooks";
import {useListViewRowDelete} from "~/components/ottopilotDataViewer/useListViewRowDelete";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import {TableDeleteConfirmationModal} from "~/components/table/TableDeleteConfirmationModal";
import {TableFooter} from "~/components/table/TableFooter";
import {TableOperationToasts} from "~/components/table/TableOperationToasts";
import {TableSelectionBar} from "~/components/table/TableSelectionBar";
import "~/components/table/table.css";

const LIST_MOBILE_BREAKPOINT = "(max-width: 47.9375em)";

function ListViewContent<
    T extends {
        id: Uuid;
    },
>({
    controller,
    view,
    className,
    rowsQuery,
    hidePageFooter = false,
    horizontalInsetClass = "px-ds-space-200 sm:px-ds-space-300",
}: {
    controller: OttopilotDataViewerController<T>;
    view: OttopilotView<T> & {
        viewType: ViewType.list;
    };
    className?: string;
    rowsQuery: ReturnType<typeof useListViewRows<T>>;
    hidePageFooter?: boolean;
    horizontalInsetClass?: string;
}) {
    const {showFlag} = useAnankeFlags();
    const isMobileViewport = useMediaQuery(LIST_MOBILE_BREAKPOINT, false, {getInitialValueInEffect: true});
    const asidePanelContext = useAsidePanelContextSafe();
    const listContainerRef = useRef<HTMLDivElement | null>(null);
    const [isNarrowContainer, setIsNarrowContainer] = useState(false);
    const [showCopyToast, setShowCopyToast] = useState(false);

    useEffect(() => {
        const element = listContainerRef.current;
        if (element === null) {
            return;
        }

        const updateWidth = (width: number) => {
            setIsNarrowContainer(width > 0 && width < LIST_CARD_NARROW_CONTAINER_MAX_WIDTH_PX);
        };

        updateWidth(element.getBoundingClientRect().width);

        if (typeof ResizeObserver === "undefined") {
            return;
        }

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry === undefined) {
                return;
            }
            updateWidth(entry.contentRect.width);
        });
        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    }, []);

    const listLayout = resolveOttopilotListCardLayout(
        isMobileViewport === true,
        controller.currentlyOpenRowEngagementPanel,
        asidePanelContext?.state ?? null,
        isNarrowContainer === true,
    );

    const loadedData = useMemo(() => {
        if (rowsQuery.status !== "success") {
            return [];
        }
        // Infinite: return rowsQuery.data.pages.flatMap((page: Array<T>) => page);
        return rowsQuery.data;
    }, [rowsQuery.status, rowsQuery.data]);

    const refetchRows = async () => {
        if (rowsQuery.status === "success") {
            await rowsQuery.refetch();
        }
    };

    const {
        visibleLoadedData,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        isDeletingSelectedRows,
        hasPendingDeleteToast,
        handleDeleteSelectedRows,
        handleConfirmDeleteFromModal,
        handleCommitDelete,
        handleUndoDelete,
        toggleRowSelected,
    } = useListViewRowDelete({
        controller,
        loadedData,
        refetchRows,
    });

    // Infinite scroll sentinel (kept for reference):
    // const sentinelRef = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const showInitialLoading = rowsQuery.status === "pending";
    const showRefetchLoading = rowsQuery.status === "success" && rowsQuery.isFetching === true;
    // Infinite: rowsQuery.isFetchingNextPage === true
    const enableTableRowSelection = controller.enableTableRowSelection !== false;
    const showDesktopSelectionBar =
        enableTableRowSelection && listLayout !== "mobile" && controller.tableSelectedRowIds.length > 0;

    const selectedRows = useMemo(
        () => visibleLoadedData.filter((row) => controller.tableSelectedRowIds.includes(String(row.id))),
        [visibleLoadedData, controller.tableSelectedRowIds],
    );
    const canTransferSelectedRows =
        controller.onRequestTransferSelectedRows === undefined
            ? false
            : (controller.canTransferSelectedRows?.(selectedRows) ?? true);

    const copyableColumns = useMemo(
        () =>
            controller.columns.lineItems.filter(
                (column) => column.id !== "__actions__" && column.id !== "__checkbox__",
            ),
        [controller.columns.lineItems],
    );

    useEffect(() => {
        if (showCopyToast !== true) {
            return;
        }
        const timeoutId = setTimeout(() => setShowCopyToast(false), 3000);
        return () => clearTimeout(timeoutId);
    }, [showCopyToast]);

    const handleCopySelectedRows = useCallback(async () => {
        if (selectedRows.length === 0 || copyableColumns.length === 0) {
            return;
        }
        try {
            const headerRow = copyableColumns.map((column) => column.displayName);
            const bodyRows = selectedRows.map((row) =>
                copyableColumns.map((column) => {
                    const rawValue = column.valueGetter(row);
                    const formattedValue =
                        column.valueFormatter !== undefined ? column.valueFormatter(rawValue) : rawValue;
                    if (formattedValue === null || formattedValue === undefined) {
                        return "";
                    }
                    if (Array.isArray(formattedValue)) {
                        return formattedValue.map((item) => String(item)).join(", ");
                    }
                    if (
                        typeof formattedValue === "string" ||
                        typeof formattedValue === "number" ||
                        typeof formattedValue === "boolean"
                    ) {
                        return String(formattedValue);
                    }
                    try {
                        return JSON.stringify(formattedValue);
                    } catch {
                        return String(formattedValue);
                    }
                }),
            );
            const tsvPayload = [headerRow, ...bodyRows]
                .map((row) => row.join("\t"))
                .join("\n");
            await navigator.clipboard.writeText(tsvPayload);
            setShowCopyToast(true);
        } catch (error) {
            showFlag({
                title: "Copy failed",
                description: error instanceof Error ? error.message : "Unable to copy selected rows.",
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 6,
            });
        }
    }, [copyableColumns, selectedRows, showFlag]);

    const pageSize = controller.pageSize > 0 ? controller.pageSize : 50;
    const totalRowsRaw = controller.rowCountQuery.status === "success" ? controller.rowCountQuery.data : null;
    const totalRows = totalRowsRaw === null ? visibleLoadedData.length : Math.max(visibleLoadedData.length, totalRowsRaw);
    const currentPage = controller.pageNumber > 0 ? controller.pageNumber : 1;
    const totalPages =
        totalRowsRaw !== null && pageSize > 0 ? Math.max(1, Math.ceil(totalRowsRaw / pageSize)) : 1;

    useEffect(() => {
        if (
            (enableTableRowSelection !== true || listLayout === "mobile") &&
            controller.tableSelectedRowIds.length > 0
        ) {
            controller.setTableSelectedRowIds([]);
        }
    }, [controller, enableTableRowSelection, listLayout]);

    // Infinite scroll IntersectionObserver (kept for reference):
    // useEffect(() => {
    //     if (rowsQuery.hasNextPage !== true) {
    //         return;
    //     }
    //     if (rowsQuery.isFetchingNextPage === true) {
    //         return;
    //     }
    //     const sentinel = sentinelRef.current;
    //     const root = scrollContainerRef.current;
    //     if (sentinel === null || root === null) {
    //         return;
    //     }
    //     const observer = new IntersectionObserver(
    //         (entries) => {
    //             const [entry] = entries;
    //             if (entry === undefined || entry.isIntersecting !== true) {
    //                 return;
    //             }
    //             if (rowsQuery.hasNextPage === true && rowsQuery.isFetchingNextPage === false) {
    //                 void rowsQuery.fetchNextPage();
    //             }
    //         },
    //         {
    //             root: root,
    //             threshold: 0,
    //             rootMargin: "200px 0px",
    //         },
    //     );
    //     observer.observe(sentinel);
    //     return () => {
    //         observer.disconnect();
    //     };
    // }, [rowsQuery.hasNextPage, rowsQuery.isFetchingNextPage, rowsQuery.fetchNextPage]);

    // Viewport mobile can use multi-column grids; container-narrow (aside open) stays 1-col.
    const listGridClassName =
        listLayout === "mobile"
            ? isMobileViewport === true
                ? "grid w-full min-w-0 grid-cols-1 gap-x-ds-space-100 gap-y-ds-space-100 content-start sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                : "grid w-full min-w-0 grid-cols-1 gap-y-ds-space-100 content-start"
            : "grid w-full min-w-0 grid-cols-1 gap-y-ds-space-100 content-start";

    return (
        <div
            ref={listContainerRef}
            className={`flex min-h-0 h-full w-full min-w-0 flex-col overflow-hidden ${className ?? ""}`}
        >
            <div className={twJoin("flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden", horizontalInsetClass)}>
                {showDesktopSelectionBar ? (
                    <div className="relative z-[2] shrink-0 w-full min-w-0">
                        <TableSelectionBar
                            selectedCount={controller.tableSelectedRowIds.length}
                            totalCount={visibleLoadedData.length}
                            onClear={() => controller.setTableSelectedRowIds([])}
                            onCopy={handleCopySelectedRows}
                            onTransfer={
                                controller.onRequestTransferSelectedRows === undefined
                                    ? undefined
                                    : () => {
                                          controller.onRequestTransferSelectedRows?.(selectedRows);
                                          controller.setTableSelectedRowIds([]);
                                      }
                            }
                            isTransferDisabled={canTransferSelectedRows !== true}
                            onDelete={handleDeleteSelectedRows}
                            isDeleteDisabled={isDeletingSelectedRows || hasPendingDeleteToast}
                        />
                    </div>
                ) : null}
                <div
                    ref={scrollContainerRef}
                    className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto w-full pb-ds-space-150"
                >
                {showInitialLoading === true ? (
                    <div className={listGridClassName}>
                        {Array.from({length: listLayout === "mobile" ? 6 : 4}, (_, index) => (
                            <KanbanCardSkeleton key={`list-skeleton-${String(index)}`} />
                        ))}
                    </div>
                ) : visibleLoadedData.length === 0 ? (
                    <TableNoDataFoundComponent />
                ) : (
                    <div className={listGridClassName}>
                        <ItemBuilder
                            items={visibleLoadedData}
                            itemBuilder={(item) => {
                                const rowId = String(item.id);
                                const isSelected = controller.tableSelectedRowIds.includes(rowId);
                                return (
                                    <Fragment key={item.id}>
                                        <div className="w-full min-w-0">
                                            {view.cardRenderer(item, {
                                                openReadPanel: () => {
                                                    if (view.onListCardClick !== undefined) {
                                                        view.onListCardClick(item);
                                                        return;
                                                    }
                                                    if (controller.readPanelComponent === undefined) {
                                                        return;
                                                    }
                                                    controller.setCurrentlyOpenRowEngagementPanel({
                                                        rowEngagementType: RowEngagementType.read,
                                                        rowEngagementPanelType: RowEngagementPanelType.sidebar,
                                                        rowEngagementData: item,
                                                    });
                                                },
                                                layout: listLayout,
                                                selection:
                                                    enableTableRowSelection && listLayout !== "mobile"
                                                        ? {
                                                              isSelected,
                                                              onToggleSelect: () => toggleRowSelected(rowId),
                                                          }
                                                        : undefined,
                                            })}
                                        </div>
                                    </Fragment>
                                );
                            }}
                        />
                        {showRefetchLoading === true ? <KanbanCardSkeleton /> : null}
                        {/* Infinite scroll sentinel (kept for reference):
                        <div ref={sentinelRef} className="h-px w-full col-span-full" />
                        */}
                    </div>
                )}
                </div>
            </div>
            {hidePageFooter !== true ? (
                <>
                    <div className="h-ds-space-250 w-full min-w-0 shrink-0" aria-hidden />
                    <div className="table-only-page-footer-border-target w-full shrink-0">
                        <div className="table-page-footer-bleed">
                            <TableFooter
                                className={horizontalInsetClass}
                                mode="paginated"
                                variant="page"
                                totalRows={totalRows}
                                totalRowsStatus={controller.rowCountQuery.status}
                                totalRowsError={
                                    controller.rowCountQuery.status === "error" ? controller.rowCountQuery.error : null
                                }
                                loadedRowCount={visibleLoadedData.length}
                                pageSize={pageSize}
                                onPageSizeChange={(size) => {
                                    controller.setPageSize(size);
                                }}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onFirstPage={() => controller.setPageNumber(1)}
                                onPreviousPage={() => controller.setPageNumber(Math.max(1, currentPage - 1))}
                                onNextPage={() => controller.setPageNumber(Math.min(totalPages, currentPage + 1))}
                                onLastPage={() => controller.setPageNumber(totalPages)}
                                onGoToPage={(page) => controller.setPageNumber(page)}
                                isFirstPage={currentPage <= 1}
                                isLastPage={currentPage >= totalPages}
                                isLoading={rowsQuery.isFetching === true}
                            />
                        </div>
                    </div>
                </>
            ) : null}
            <TableDeleteConfirmationModal
                opened={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDeleteFromModal}
            />
            <TableOperationToasts
                showCopyToast={showCopyToast}
                onDismissCopyToast={() => setShowCopyToast(false)}
                showPendingDeleteToast={hasPendingDeleteToast}
                onDismissPendingDeleteToast={() => {
                    void handleCommitDelete();
                }}
                onUndoDelete={handleUndoDelete}
            />
        </div>
    );
}

export function ListView<
    T extends {
        id: Uuid;
    },
>({
    controller,
    view,
    className,
    hidePageFooter = false,
    horizontalInsetClass,
}: {
    controller: OttopilotDataViewerController<T>;
    view: OttopilotView<T> & {
        viewType: ViewType.list;
    };
    className?: string;
    hidePageFooter?: boolean;
    horizontalInsetClass?: string;
}) {
    return (
        <ListViewInfiniteRows controller={controller}>
            {(rowsQuery) => (
                <ListViewContent
                    controller={controller}
                    view={view}
                    className={className}
                    rowsQuery={rowsQuery}
                    hidePageFooter={hidePageFooter}
                    horizontalInsetClass={horizontalInsetClass}
                />
            )}
        </ListViewInfiniteRows>
    );
}
